declare var FroalaEditor: any;

const tableBorderStyleLabels: Record<string, string> = {
  noborder: 'Remove Border',
  addborder: 'Add Border',
  dashedborder: 'Dashed Border'
};

const tableBorderStyleClasses = Object.keys(
  tableBorderStyleLabels
);

/**
 * IMPORTANT:
 * "noborder" is a command/state, not an actual border.
 *
 * Remove Border restores the original border captured in
 * data-blueprint-base-border-* attributes.
 */
const tableBorderStyles: Record<string, string> = {
  noborder: 'none',
  addborder: '1px solid #000',
  dashedborder: '1px dashed #000'
};

const appliedTableBorderStyleClasses = [
  'addborder',
  'dashedborder'
];

/**
 * Neutral default table gridline. This is the same light border a table
 * shows when it is first inserted (and the value the `.noborder` CSS rule
 * and the report's default-border mapping render). Remove Border reverts
 * cells to this gridline rather than hiding their borders entirely.
 */
const NEUTRAL_TABLE_GRIDLINE = '1px solid #DDD';

const tableCellStyleLabels: Record<string, string> = {
  'fr-highlighted': 'Highlighted',
  'fr-thick': 'Thick'
};

const tableCellStyleClasses = Object.keys(
  tableCellStyleLabels
);

const tableCellVerticalAlignLabels: Record<string, string> = {
  top: 'Top',
  middle: 'Middle',
  bottom: 'Bottom'
};

const tableCellVerticalAlignValues = Object.keys(
  tableCellVerticalAlignLabels
);

const tableCellVerticalAlignClassPrefix =
  'blueprint-cell-valign-';

const tableCellVerticalAlignClasses =
  tableCellVerticalAlignValues.map(
    alignValue =>
      `${tableCellVerticalAlignClassPrefix}${alignValue}`
  );

const tableCellVerticalAlignContentClass =
  'blueprint-cell-valign-content';

const tableSelectionVisualClass =
  'fr-selected-cell';

const legacyTableSelectionVisualClass =
  'blueprint-table-selected-cell';

const legacyTableSelectionNeighborClassPrefix =
  'blueprint-selected-neighbor-';

const legacyTableSelectionNeighborClasses = [
  'top',
  'right',
  'bottom',
  'left'
].map(
  side =>
    `${legacyTableSelectionNeighborClassPrefix}${side}`
);

const tableBaseBorderAttributePrefix =
  'data-blueprint-base-border-';

const rememberedTableCellsByEditor =
  new WeakMap<object, HTMLElement[]>();

const pendingCommandTableCellsByEditor =
  new WeakMap<object, HTMLElement[]>();

const tableSelectionAnchorCellByEditor =
  new WeakMap<object, HTMLElement>();

const tableSelectionDragActiveByEditor =
  new WeakMap<object, boolean>();

const tableSelectionCleanupByEditor =
  new WeakMap<object, () => void>();

type TableBorderSide =
  | 'top'
  | 'right'
  | 'bottom'
  | 'left';

type TableGrid =
  Array<Array<HTMLTableCellElement | null>>;


/* =========================================================
 * EDITOR / CELL HELPERS
 * ========================================================= */

function getEditorMemoryKey(editor: any): object | null {
  if (
    editor &&
    (
      typeof editor === 'object' ||
      typeof editor === 'function'
    )
  ) {
    return editor;
  }

  return null;
}

function filterUsableTableCells(
  editor: any,
  cells: HTMLElement[]
): HTMLElement[] {
  const editorElement =
    editor?.el as HTMLElement | undefined;

  const seenCells =
    new Set<HTMLElement>();

  return cells.filter((cell) => {
    if (
      !cell?.matches?.('td, th') ||
      (
        editorElement &&
        !editorElement.contains(cell)
      )
    ) {
      return false;
    }

    if (seenCells.has(cell)) {
      return false;
    }

    seenCells.add(cell);

    return true;
  });
}

function isHtmlElement(
  value: any
): value is HTMLElement {
  return !!value &&
    value.nodeType === 1 &&
    typeof value.matches === 'function';
}

function toElementArray(
  collection: any
): HTMLElement[] {
  if (!collection) {
    return [];
  }

  if (isHtmlElement(collection)) {
    return [collection];
  }

  if (Array.isArray(collection)) {
    return collection.filter(isHtmlElement);
  }

  if (typeof collection.toArray === 'function') {
    return collection
      .toArray()
      .filter(isHtmlElement);
  }

  if (typeof collection.length === 'number') {
    return Array.from({
      length: collection.length
    })
      .map(
        (_, index) =>
          collection[index] ||
          collection.get?.(index)
      )
      .filter(isHtmlElement);
  }

  return [];
}


/* =========================================================
 * TABLE CELL MEMORY
 * ========================================================= */

function rememberFroalaTableCells(
  editor: any,
  cells: HTMLElement[]
): void {
  const editorKey =
    getEditorMemoryKey(editor);

  if (!editorKey) {
    return;
  }

  const usableCells =
    filterUsableTableCells(
      editor,
      cells
    );

  if (usableCells.length) {
    rememberedTableCellsByEditor.set(
      editorKey,
      usableCells
    );
  }
}

function getRememberedFroalaTableCells(
  editor: any
): HTMLElement[] {
  const editorKey =
    getEditorMemoryKey(editor);

  if (!editorKey) {
    return [];
  }

  const rememberedCells =
    filterUsableTableCells(
      editor,
      rememberedTableCellsByEditor.get(
        editorKey
      ) || []
    );

  if (!rememberedCells.length) {
    rememberedTableCellsByEditor.delete(
      editorKey
    );
  }

  return rememberedCells;
}

function rememberFroalaTableSelectionAnchor(
  editor: any,
  cell: HTMLElement
): void {
  const editorKey =
    getEditorMemoryKey(editor);

  if (
    !editorKey ||
    !filterUsableTableCells(
      editor,
      [cell]
    ).length
  ) {
    return;
  }

  tableSelectionAnchorCellByEditor.set(
    editorKey,
    cell
  );
}

function getRememberedFroalaTableSelectionAnchor(
  editor: any
): HTMLElement | null {
  const editorKey =
    getEditorMemoryKey(editor);

  if (!editorKey) {
    return null;
  }

  const anchorCell =
    tableSelectionAnchorCellByEditor.get(
      editorKey
    );

  if (
    !anchorCell ||
    !filterUsableTableCells(
      editor,
      [anchorCell]
    ).length
  ) {
    tableSelectionAnchorCellByEditor.delete(
      editorKey
    );

    return null;
  }

  return anchorCell;
}

function setFroalaTableSelectionDragActive(
  editor: any,
  isActive: boolean
): void {
  const editorKey =
    getEditorMemoryKey(editor);

  if (!editorKey) {
    return;
  }

  if (isActive) {
    tableSelectionDragActiveByEditor.set(
      editorKey,
      true
    );

    return;
  }

  tableSelectionDragActiveByEditor.delete(
    editorKey
  );
}

function isFroalaTableSelectionDragActive(
  editor: any
): boolean {
  const editorKey =
    getEditorMemoryKey(editor);

  return !!editorKey &&
    tableSelectionDragActiveByEditor.get(
      editorKey
    ) === true;
}


/* =========================================================
 * PENDING CELL MEMORY
 * ========================================================= */

function setPendingFroalaTableCells(
  editor: any,
  cells: HTMLElement[]
): void {
  const editorKey =
    getEditorMemoryKey(editor);

  if (!editorKey) {
    return;
  }

  const usableCells =
    filterUsableTableCells(
      editor,
      cells
    );

  if (!usableCells.length) {
    pendingCommandTableCellsByEditor.delete(
      editorKey
    );

    return;
  }

  pendingCommandTableCellsByEditor.set(
    editorKey,
    usableCells
  );

  setTimeout(() => {
    if (
      pendingCommandTableCellsByEditor.get(
        editorKey
      ) === usableCells
    ) {
      pendingCommandTableCellsByEditor.delete(
        editorKey
      );
    }
  }, 1000);
}

function getPendingFroalaTableCells(
  editor: any
): HTMLElement[] {
  const editorKey =
    getEditorMemoryKey(editor);

  if (!editorKey) {
    return [];
  }

  const pendingCells =
    filterUsableTableCells(
      editor,
      pendingCommandTableCellsByEditor.get(
        editorKey
      ) || []
    );

  if (!pendingCells.length) {
    pendingCommandTableCellsByEditor.delete(
      editorKey
    );
  }

  return pendingCells;
}


/* =========================================================
 * SELECTION VISUALS
 * ========================================================= */

function removeTableSelectionVisualClasses(
  cell: Element
): void {
  /*
   * IMPORTANT:
   *
   * Only remove selection-related classes here.
   *
   * Do NOT touch border styles.
   */
  cell.classList.remove(
    tableSelectionVisualClass,
    'fr-cell-fixed',
    'fr-cell-handler',
    legacyTableSelectionVisualClass,
    ...legacyTableSelectionNeighborClasses
  );
}

function restoreFroalaEditableState(
  editor: any,
  editorElement?: HTMLElement
): void {
  editorElement?.classList.remove(
    'fr-no-selection'
  );

  editor?.$el?.removeClass?.(
    'fr-no-selection'
  );

  editor?.edit?.on?.();
  editor?.toolbar?.enable?.();
}

function restoreFroalaSelectedTableCells(
  editor: any,
  cells: HTMLElement[]
): void {
  const editorElement =
    editor?.el as HTMLElement | undefined;

  if (!editorElement) {
    return;
  }

  const usableCells =
    filterUsableTableCells(
      editor,
      cells
    );

  if (!usableCells.length) {
    return;
  }

  /*
   * Remove all previous Froala selection visuals.
   *
   * IMPORTANT:
   * This removes only selection classes.
   * It does NOT modify the actual table borders.
   */
  editorElement
    .querySelectorAll(
      '.fr-selected-cell, ' +
      '.fr-cell-fixed, ' +
      '.fr-cell-handler, ' +
      '.blueprint-table-selected-cell, ' +
      '[class*="blueprint-selected-neighbor-"]'
    )
    .forEach((cell) => {
      if (
        isHtmlElement(cell) &&
        cell.matches('td, th')
      ) {
        removeTableSelectionVisualClasses(
          cell
        );
      }
    });

  /*
   * Add Froala selection visual ONLY to the
   * cells belonging to the CURRENT selection.
   */
  usableCells.forEach((cell) => {
    cell.classList.add(
      tableSelectionVisualClass
    );
  });

  restoreFroalaEditableState(
    editor,
    editorElement
  );
}

/**
 * Clears only the visual selection state.
 *
 * IMPORTANT:
 * This function does NOT:
 * - change borders
 * - remove inline border styles
 * - restore/remove base borders
 * - clear selection memory
 *
 * It only removes Froala selection visuals.
 */
function clearFroalaTableSelectionVisualOnly(
  editor: any
): void {
  const editorElement =
    editor?.el as HTMLElement | undefined;

  if (!editorElement) {
    return;
  }

  editorElement
    .querySelectorAll(
      '.fr-selected-cell, ' +
      '.fr-cell-fixed, ' +
      '.fr-cell-handler, ' +
      '.blueprint-table-selected-cell, ' +
      '[class*="blueprint-selected-neighbor-"]'
    )
    .forEach((cell) => {
      if (
        isHtmlElement(cell) &&
        cell.matches('td, th')
      ) {
        removeTableSelectionVisualClasses(
          cell
        );
      }
    });
}

function clearFroalaTableSelectionVisual(
  editor: any,
  clearMemory = true
): void {
  clearFroalaTableSelectionVisualOnly(
    editor
  );

  if (clearMemory) {
    clearFroalaTableSelectionMemory(
      editor
    );
  }
}

export function clearFroalaTableSelectionMemory(
  editor: any
): void {
  const editorKey =
    getEditorMemoryKey(editor);

  if (!editorKey) {
    return;
  }

  /*
   * Clear only selection visuals.
   *
   * Actual table borders remain untouched.
   */
  clearFroalaTableSelectionVisualOnly(
    editor
  );

  rememberedTableCellsByEditor.delete(
    editorKey
  );

  pendingCommandTableCellsByEditor.delete(
    editorKey
  );

  tableSelectionAnchorCellByEditor.delete(
    editorKey
  );

  tableSelectionDragActiveByEditor.delete(
    editorKey
  );
}


/* =========================================================
 * TABLE SELECTION
 * ========================================================= */

function getSourceTableCell(
  editor: any,
  source?: Event | HTMLElement | null
): HTMLElement | null {
  const target =
    isHtmlElement(source)
      ? source
      : source?.target;

  if (!isHtmlElement(target)) {
    return null;
  }

  const cell =
    target.closest(
      'td, th'
    ) as HTMLElement | null;

  return cell &&
    filterUsableTableCells(
      editor,
      [cell]
    ).length
    ? cell
    : null;
}

function getLiveFroalaTableCells(
  editor: any
): HTMLElement[] {
  const selectedCells = [
    ...toElementArray(
      editor.table?.selectedCells?.()
    ),
    ...toElementArray(
      editor.$el?.find?.(
        '.fr-cell-fixed, .fr-cell-handler'
      )
    )
  ];

  const expandedSelectedCells =
    expandFroalaTableCellSelection(
      selectedCells.filter(
        cell =>
          cell.matches('td, th')
      )
    );

  if (expandedSelectedCells.length) {
    return expandedSelectedCells;
  }

  const froalaSelectedCells =
    expandFroalaTableCellSelection(
      toElementArray(
        editor.$el?.find?.(
          '.fr-selected-cell, ' +
          '.fr-cell-fixed, ' +
          '.fr-cell-handler'
        )
      ).filter(
        cell =>
          cell.matches('td, th')
      )
    );

  if (froalaSelectedCells.length) {
    return froalaSelectedCells;
  }

  const selectionElement =
    editor.selection?.element?.() as HTMLElement | null;

  const currentCell =
    selectionElement?.closest?.(
      'td, th'
    ) as HTMLElement | null;

  return currentCell
    ? [currentCell]
    : [];
}

function mergeSelectionAnchor(
  editor: any,
  cells: HTMLElement[]
): HTMLElement[] {
  const anchorCell =
    getRememberedFroalaTableSelectionAnchor(
      editor
    );

  if (
    !anchorCell ||
    cells.includes(anchorCell)
  ) {
    return cells;
  }

  const anchorTable =
    anchorCell.closest('table');

  if (
    !anchorTable ||
    !cells.some(
      cell =>
        cell.closest('table') ===
        anchorTable
    )
  ) {
    return cells;
  }

  return expandFroalaTableCellSelection([
    anchorCell,
    ...cells
  ]);
}

function expandFroalaTableCellSelection(
  cells: HTMLElement[]
): HTMLElement[] {
  const cellsByTable =
    new Map<
      HTMLTableElement,
      HTMLTableCellElement[]
    >();

  const expandedCells:
    HTMLTableCellElement[] = [];

  cells.forEach((cell) => {
    const table =
      cell.closest(
        'table'
      ) as HTMLTableElement | null;

    if (
      !table ||
      !cell.matches('td, th')
    ) {
      return;
    }

    const tableCells =
      cellsByTable.get(table) || [];

    tableCells.push(
      cell as HTMLTableCellElement
    );

    cellsByTable.set(
      table,
      tableCells
    );
  });

  cellsByTable.forEach(
    (tableCells, table) => {
      if (tableCells.length === 1) {
        expandedCells.push(
          tableCells[0]
        );

        return;
      }

      const grid =
        buildTableGrid(table);

      let minRow =
        Number.POSITIVE_INFINITY;

      let maxRow = -1;

      let minColumn =
        Number.POSITIVE_INFINITY;

      let maxColumn = -1;

      grid.forEach(
        (row, rowIndex) => {
          row.forEach(
            (
              gridCell,
              columnIndex
            ) => {
              if (
                !gridCell ||
                !tableCells.includes(
                  gridCell
                )
              ) {
                return;
              }

              minRow = Math.min(
                minRow,
                rowIndex
              );

              maxRow = Math.max(
                maxRow,
                rowIndex
              );

              minColumn = Math.min(
                minColumn,
                columnIndex
              );

              maxColumn = Math.max(
                maxColumn,
                columnIndex
              );
            }
          );
        }
      );

      if (
        maxRow < 0 ||
        maxColumn < 0
      ) {
        expandedCells.push(
          ...tableCells
        );

        return;
      }

      for (
        let rowIndex = minRow;
        rowIndex <= maxRow;
        rowIndex++
      ) {
        for (
          let columnIndex = minColumn;
          columnIndex <= maxColumn;
          columnIndex++
        ) {
          const gridCell =
            grid[rowIndex]?.[
              columnIndex
            ];

          if (
            gridCell &&
            !expandedCells.includes(
              gridCell
            )
          ) {
            expandedCells.push(
              gridCell
            );
          }
        }
      }
    }
  );

  return expandedCells;
}

function rememberFroalaTableCellRange(
  editor: any,
  targetCell: HTMLElement
): void {
  const anchorCell =
    getRememberedFroalaTableSelectionAnchor(
      editor
    );

  const anchorTable =
    anchorCell?.closest('table');

  const targetTable =
    targetCell.closest('table');

  const cells =
    anchorCell &&
    anchorTable &&
    anchorTable === targetTable
      ? expandFroalaTableCellSelection([
          anchorCell,
          targetCell
        ])
      : [targetCell];

  setPendingFroalaTableCells(
    editor,
    cells
  );

  rememberFroalaTableCells(
    editor,
    cells
  );

  restoreFroalaSelectedTableCells(
    editor,
    cells
  );
}

function isFroalaTablePopupVisible(
  editor: any
): boolean {
  return !!editor?.popups?.isVisible?.(
    'table.edit'
  );
}

export function rememberCurrentFroalaTableCells(
  editor: any,
  source?: Event | HTMLElement | null
): void {
  const liveCells =
    getLiveFroalaTableCells(editor);


    
  const sourceCell =
    getSourceTableCell(
      editor,
      source
    );

  if (sourceCell) {
    rememberFroalaTableSelectionAnchor(
      editor,
      sourceCell
    );
  }

  const sourceTable =
    sourceCell?.closest('table');

  const liveCellsInSourceTable =
    sourceTable
      ? liveCells.filter(
          cell =>
            cell.closest('table') ===
            sourceTable
        )
      : liveCells;

  const cells = sourceCell
    ? (
        liveCellsInSourceTable.length
          ? expandFroalaTableCellSelection([
              sourceCell,
              ...liveCellsInSourceTable
            ])
          : [sourceCell]
      )
    : mergeSelectionAnchor(
        editor,
        liveCells
      );

  if (!cells.length) {
    return;
  }

  setPendingFroalaTableCells(
    editor,
    cells
  );

  rememberFroalaTableCells(
    editor,
    cells
  );
}

export function handleFroalaTableMouseDown(
  editor: any,
  event?: Event
): void {
  const sourceCell =
    getSourceTableCell(
      editor,
      event
    );

  if (sourceCell) {
    const currentAnchor =
      getRememberedFroalaTableSelectionAnchor(
        editor
      );

    const shouldExtendExistingRange =
      !!(
        event instanceof MouseEvent &&
        event.shiftKey &&
        currentAnchor &&
        currentAnchor.closest('table') ===
          sourceCell.closest('table')
      );

    /*
     * IMPORTANT:
     *
     * Normal click:
     * Clear the previous selection BEFORE
     * creating the new selection.
     *
     * This prevents the previous cell from
     * retaining .fr-selected-cell.
     *
     * Shift + click:
     * Preserve the existing selection and extend it.
     */
    if (!shouldExtendExistingRange) {
      clearFroalaTableSelectionMemory(
        editor
      );

      rememberFroalaTableSelectionAnchor(
        editor,
        sourceCell
      );
    }

    setFroalaTableSelectionDragActive(
      editor,
      true
    );

    rememberFroalaTableCellRange(
      editor,
      sourceCell
    );

    return;
  }

  setFroalaTableSelectionDragActive(
    editor,
    false
  );

  clearFroalaTableSelectionMemory(
    editor
  );
}

export function handleFroalaTableMouseOver(
  editor: any,
  event?: Event
): void {
  if (
    !isFroalaTableSelectionDragActive(
      editor
    )
  ) {
    return;
  }

  const sourceCell =
    getSourceTableCell(
      editor,
      event
    );

  if (sourceCell) {
    rememberFroalaTableCellRange(
      editor,
      sourceCell
    );
  }
}

export function handleFroalaTableMouseUp(
  editor: any,
  event?: Event
): void {
  if (
    !isFroalaTableSelectionDragActive(
      editor
    )
  ) {
    return;
  }

  const sourceCell =
    getSourceTableCell(
      editor,
      event
    );

  if (sourceCell) {
    rememberFroalaTableCellRange(
      editor,
      sourceCell
    );
  }

  setFroalaTableSelectionDragActive(
    editor,
    false
  );
}

export function bindFroalaTableSelectionHandlers(
  editor: any
): void {
  const editorKey =
    getEditorMemoryKey(editor);

  const editorElement =
    editor?.el as HTMLElement | undefined;

  if (
    !editorKey ||
    !editorElement ||
    tableSelectionCleanupByEditor.has(
      editorKey
    )
  ) {
    return;
  }

  const editorWindow =
    editorElement.ownerDocument?.defaultView;

  const onMouseDown = (
    event: MouseEvent
  ) =>
    handleFroalaTableMouseDown(
      editor,
      event
    );

  const onMouseOver = (
    event: MouseEvent
  ) =>
    handleFroalaTableMouseOver(
      editor,
      event
    );

  const onMouseUp = (
    event: MouseEvent
  ) => {
    handleFroalaTableMouseUp(
      editor,
      event
    );

    /*
     * Do not immediately recreate the selection
     * from Froala's stale internal selected-cell
     * state.
     *
     * A normal mouse-up has already established
     * the current selection.
     */
    setTimeout(
      () =>
        syncFroalaTableSelectionVisual(
          editor
        ),
      0
    );
  };

  const onDocumentMouseDown = (
    event: MouseEvent
  ) => {
    const target =
      event.target;

    if (!isHtmlElement(target)) {
      return;
    }

    if (
      editorElement.contains(target) ||
      target.closest(
        '.fr-toolbar, .fr-popup, .fr-modal'
      )
    ) {
      return;
    }

    clearFroalaTableSelectionMemory(
      editor
    );
  };

  const cleanup = () => {
    editorElement.removeEventListener(
      'mousedown',
      onMouseDown,
      true
    );

    editorElement.removeEventListener(
      'mouseover',
      onMouseOver,
      true
    );

    editorElement.removeEventListener(
      'mouseup',
      onMouseUp,
      true
    );

    editorWindow?.removeEventListener(
      'mouseup',
      onMouseUp,
      true
    );

    editorElement.ownerDocument
      ?.removeEventListener(
        'mousedown',
        onDocumentMouseDown,
        true
      );

    tableSelectionCleanupByEditor.delete(
      editorKey
    );

    setFroalaTableSelectionDragActive(
      editor,
      false
    );
  };

  editorElement.addEventListener(
    'mousedown',
    onMouseDown,
    true
  );

  editorElement.addEventListener(
    'mouseover',
    onMouseOver,
    true
  );

  editorElement.addEventListener(
    'mouseup',
    onMouseUp,
    true
  );

  editorWindow?.addEventListener(
    'mouseup',
    onMouseUp,
    true
  );

  editorElement.ownerDocument
    ?.addEventListener(
      'mousedown',
      onDocumentMouseDown,
      true
    );

  tableSelectionCleanupByEditor.set(
    editorKey,
    cleanup
  );

  editor.events?.on?.(
    'destroy',
    cleanup
  );
}

function syncFroalaTableSelectionVisual(
  editor: any
): void {
  /*
   * IMPORTANT:
   *
   * Do not recreate the visual selection after
   * mouse-up from Froala's internal selected-cell
   * state.
   *
   * Otherwise Froala can re-add .fr-selected-cell
   * to a cell after Remove Border has already
   * cleared it.
   */
  if (
    !isFroalaTableSelectionDragActive(
      editor
    )
  ) {
    return;
  }

  const liveCells =
    getLiveFroalaTableCells(editor);

  if (!liveCells.length) {
    return;
  }

  const selectedCells =
    mergeSelectionAnchor(
      editor,
      liveCells
    );

  rememberFroalaTableCells(
    editor,
    selectedCells
  );

  restoreFroalaSelectedTableCells(
    editor,
    selectedCells
  );
}

function restoreFroalaTableSelectionAfterCommand(
  editor: any,
  cells: HTMLElement[]
): void {
  const usableCells =
    filterUsableTableCells(
      editor,
      cells
    );

  if (!usableCells.length) {
    return;
  }

  setPendingFroalaTableCells(
    editor,
    usableCells
  );

  rememberFroalaTableCells(
    editor,
    usableCells
  );

  restoreFroalaSelectedTableCells(
    editor,
    usableCells
  );

  setTimeout(
    () =>
      restoreFroalaSelectedTableCells(
        editor,
        usableCells
      ),
    0
  );
}

export function getSelectedFroalaTableCells(
  editor: any
): HTMLElement[] {
  const liveCells =
    getLiveFroalaTableCells(editor);

  if (liveCells.length) {
    const selectedCells =
      mergeSelectionAnchor(
        editor,
        liveCells
      );

    rememberFroalaTableCells(
      editor,
      selectedCells
    );

    return selectedCells;
  }

  const pendingCells =
    getPendingFroalaTableCells(
      editor
    );

  if (pendingCells.length) {
    const selectedCells =
      mergeSelectionAnchor(
        editor,
        pendingCells
      );

    restoreFroalaSelectedTableCells(
      editor,
      selectedCells
    );

    return selectedCells;
  }

  if (
    isFroalaTablePopupVisible(
      editor
    )
  ) {
    const rememberedCells =
      getRememberedFroalaTableCells(
        editor
      );

    if (rememberedCells.length) {
      const selectedCells =
        mergeSelectionAnchor(
          editor,
          rememberedCells
        );

      restoreFroalaSelectedTableCells(
        editor,
        selectedCells
      );

      return selectedCells;
    }
  }

  return [];
}


/* =========================================================
 * BORDER MANAGEMENT
 * ========================================================= */

/**
 * Capture ONLY the border actually owned by the cell.
 *
 * DO NOT use getComputedStyle() here.
 *
 * With border-collapse: collapse, computed styles can contain
 * the resolved border contributed by another neighbouring cell.
 */
function rememberBaseTableCellBorders(
  cell: HTMLElement
): void {
  (
    [
      'top',
      'right',
      'bottom',
      'left'
    ] as TableBorderSide[]
  ).forEach((side) => {
    const attributeName =
      `${tableBaseBorderAttributePrefix}${side}`;

    /*
     * Never overwrite the original snapshot.
     */
    if (cell.hasAttribute(attributeName)) {
      return;
    }

    const borderValue =
      getExplicitCellBorderSide(
        cell,
        side
      );

    cell.setAttribute(
      attributeName,
      borderValue
    );
  });
}

/**
 * Reads only borders explicitly owned by the cell.
 *
 * We intentionally DO NOT call getComputedStyle().
 */
function getExplicitCellBorderSide(
  cell: HTMLElement,
  side: TableBorderSide
): string {
  const property =
    `border-${side}`;

  /*
   * 1. Specific inline property.
   */
  const inlineValue =
    cell.style
      .getPropertyValue(property)
      .trim();

  if (inlineValue) {
    return inlineValue;
  }

  /*
   * 2. Inline shorthand.
   */
  const inlineBorder =
    cell.style
      .getPropertyValue('border')
      .trim();

  if (inlineBorder) {
    return inlineBorder;
  }

  /*
   * 3. Raw style attribute fallback.
   */
  const styleAttribute =
    cell.getAttribute('style') || '';

  const escapedProperty =
    property.replace(
      /[-/\\^$*+?.()|[\]{}]/g,
      '\\$&'
    );

  const match =
    styleAttribute.match(
      new RegExp(
        `${escapedProperty}\\s*:\\s*([^;]+)`,
        'i'
      )
    );

  if (match?.[1]) {
    return match[1].trim();
  }

  /*
   * No explicit border owned by this cell.
   */
  return '';
}

function restoreBaseTableCellBorders(
  cell: HTMLElement
): void {
  cell.style.removeProperty(
    'border'
  );

  (
    [
      'top',
      'right',
      'bottom',
      'left'
    ] as TableBorderSide[]
  ).forEach((side) => {
    cell.style.removeProperty(
      `border-${side}`
    );
  });

  (
    [
      'top',
      'right',
      'bottom',
      'left'
    ] as TableBorderSide[]
  ).forEach((side) => {
    restoreBaseTableCellBorderSide(
      cell,
      side
    );
  });
}

function restoreBaseTableCellBorderSide(
  cell: HTMLElement,
  side: TableBorderSide
): void {
  const attributeName =
    `${tableBaseBorderAttributePrefix}${side}`;

  const borderValue =
    cell.getAttribute(
      attributeName
    );

  if (borderValue) {
    cell.style.setProperty(
      `border-${side}`,
      borderValue,
      'important'
    );
  } else {
    cell.style.removeProperty(
      `border-${side}`
    );
  }

  cell.removeAttribute(
    attributeName
  );
}

function setCellBorder(
  cell: HTMLElement,
  border: string
): void {
  cell.style.setProperty(
    'border',
    border,
    'important'
  );

  cell.style.setProperty(
    'border-top',
    border,
    'important'
  );

  cell.style.setProperty(
    'border-right',
    border,
    'important'
  );

  cell.style.setProperty(
    'border-bottom',
    border,
    'important'
  );

  cell.style.setProperty(
    'border-left',
    border,
    'important'
  );
}

function setCellBorderSide(
  cell: HTMLElement,
  side: TableBorderSide,
  border: string
): void {
  cell.style.setProperty(
    `border-${side}`,
    border,
    'important'
  );
}

function hasAppliedTableBorderClass(
  cell: HTMLElement
): boolean {
  return appliedTableBorderStyleClasses.some(
    styleClass =>
      cell.classList.contains(
        styleClass
      )
  );
}

function getBaseCellBorder(
  cell: HTMLElement,
  fallbackBorder = '1px solid #000'
): string {
  const borderClass =
    appliedTableBorderStyleClasses.find(
      styleClass =>
        cell.classList.contains(
          styleClass
        )
    );

  return borderClass
    ? tableBorderStyles[borderClass]
    : fallbackBorder;
}

function applyEffectiveTableCellBorder(
  cell: HTMLElement,
  fallbackBorder?: string
): void {
  if (
    cell.classList.contains(
      'fr-highlighted'
    )
  ) {
    setCellBorder(
      cell,
      '1px double red'
    );

    return;
  }

  if (
    cell.classList.contains(
      'fr-thick'
    )
  ) {
    const baseBorder =
      getBaseCellBorder(
        cell,
        fallbackBorder
      );

    const thickBorder =
      baseBorder === 'none'
        ? '2px solid #000'
        : baseBorder.replace(
            /^\S+/,
            '2px'
          );

    setCellBorder(
      cell,
      thickBorder
    );

    return;
  }

  setCellBorder(
    cell,
    getBaseCellBorder(
      cell,
      fallbackBorder
    )
  );
}

function normalizeTableFrameForCellBorders(
  table: HTMLTableElement
): void {
  table.removeAttribute('border');
  table.removeAttribute('frame');
  table.removeAttribute('rules');
  table.removeAttribute('bordercolor');

  /*
   * Kill any frame border owned by the <table> itself so it
   * cannot re-introduce an outer border after cell borders are
   * removed. Cell edges are managed per-cell below.
   */
  table.style.setProperty(
    'border',
    'none',
    'important'
  );

  (
    [
      'top',
      'right',
      'bottom',
      'left'
    ] as TableBorderSide[]
  ).forEach((side) => {
    table.style.setProperty(
      `border-${side}`,
      'none',
      'important'
    );
  });

  table.style.setProperty(
    'border-collapse',
    'collapse'
  );

  table.style.setProperty(
    'border-spacing',
    '0'
  );
}


/* =========================================================
 * APPLY / REMOVE TABLE BORDER
 * ========================================================= */

export function applyFroalaTableBorderStyleToSelection(
  editor: any,
  styleClass: string
): boolean {
  if (
    !tableBorderStyleClasses.includes(
      styleClass
    )
  ) {
    return false;
  }

  const selectedCells =
    getSelectedFroalaTableCells(
      editor
    );

  if (!selectedCells.length) {
    return false;
  }

  const isRemoveBorder =
    styleClass === 'noborder';

  const border =
    tableBorderStyles[styleClass];

  selectedCells.forEach((cell) => {
    const table =
      cell.closest(
        'table'
      ) as HTMLTableElement | null;

    if (table) {
      tableBorderStyleClasses.forEach(
        borderClass =>
          table.classList.remove(
            borderClass
          )
      );

      normalizeTableFrameForCellBorders(
        table
      );
    }

    tableBorderStyleClasses.forEach(
      borderClass =>
        cell.classList.remove(
          borderClass
        )
    );

    tableCellStyleClasses.forEach(
      cellStyleClass =>
        cell.classList.remove(
          cellStyleClass
        )
    );

    if (isRemoveBorder) {
      /*
       * Remove Border reverts the cell to the neutral default
       * gridline (1px solid #DDD), NOT a fully invisible cell.
       * Write that gridline explicitly onto the selected cell so
       * no stale top/right/bottom/left edge can survive in the
       * saved HTML or Preview reconstruction path.
       */
      cell.classList.add(
        'noborder'
      );

      setCellBorder(
        cell,
        NEUTRAL_TABLE_GRIDLINE
      );

      /*
       * Drop any base-border snapshot captured while a
       * border was applied so it is not persisted to the
       * saved report HTML.
       */
      (
        [
          'top',
          'right',
          'bottom',
          'left'
        ] as TableBorderSide[]
      ).forEach(
        side =>
          cell.removeAttribute(
            `${tableBaseBorderAttributePrefix}${side}`
          )
      );

      return;
    }

    /*
     * Add Border / Dashed Border.
     *
     * Remember the original border before
     * replacing it.
     */
    rememberBaseTableCellBorders(
      cell
    );

    cell.classList.add(
      styleClass
    );

    applyEffectiveTableCellBorder(
      cell,
      border
    );
  });

  /*
   * Synchronise neighbour edges for both add and remove so a
   * collapsed table stays consistent. Add Border pushes its
   * border onto adjacent unselected cells; Remove Border pushes
   * the neutral gridline back so the shared edges (and the
   * Preview grid, which derives each edge from a single owning
   * cell) keep showing the default gridline instead of a black
   * border or a gap.
   */
  syncCollapsedTableNeighborEdges(
    selectedCells,
    isRemoveBorder
      ? NEUTRAL_TABLE_GRIDLINE
      : border,
    isRemoveBorder
  );

  /*
   * IMPORTANT:
   *
   * Remove Border should restore the actual
   * original border AND remove Froala's blue
   * selection visual.
   *
   * We intentionally do NOT call
   * restoreFroalaTableSelectionAfterCommand()
   * here because that would re-add .fr-selected-cell.
   */
  if (isRemoveBorder) {
    clearFroalaTableSelectionVisualOnly(
      editor
    );
  } else {
    restoreFroalaTableSelectionAfterCommand(
      editor,
      selectedCells
    );
  }

  return true;
}


/* =========================================================
 * NEIGHBOUR BORDER SYNCHRONIZATION
 * ========================================================= */

function syncCollapsedTableNeighborEdges(
  selectedCells: HTMLElement[],
  border: string,
  isRemoveBorder = false
): void {
  const cellsByTable =
    new Map<
      HTMLTableElement,
      HTMLTableCellElement[]
    >();

  selectedCells.forEach((cell) => {
    const table =
      cell.closest(
        'table'
      ) as HTMLTableElement | null;

    if (
      !table ||
      !(cell instanceof HTMLTableCellElement)
    ) {
      return;
    }

    const tableCells =
      cellsByTable.get(table) || [];

    tableCells.push(cell);

    cellsByTable.set(
      table,
      tableCells
    );
  });

  cellsByTable.forEach(
    (tableSelectedCells, table) => {
      const grid =
        buildTableGrid(table);

      const selectedSet =
        new Set(
          tableSelectedCells
        );

      grid.forEach(
        (row, rowIndex) => {
          row.forEach(
            (
              cell,
              columnIndex
            ) => {
              if (
                !cell ||
                !selectedSet.has(cell)
              ) {
                return;
              }

              syncNeighborEdge(
                grid[rowIndex - 1]?.[
                  columnIndex
                ],
                cell,
                selectedSet,
                'bottom',
                border,
                isRemoveBorder
              );

              syncNeighborEdge(
                grid[rowIndex + 1]?.[
                  columnIndex
                ],
                cell,
                selectedSet,
                'top',
                border,
                isRemoveBorder
              );

              syncNeighborEdge(
                row[columnIndex - 1],
                cell,
                selectedSet,
                'right',
                border,
                isRemoveBorder
              );

              syncNeighborEdge(
                row[columnIndex + 1],
                cell,
                selectedSet,
                'left',
                border,
                isRemoveBorder
              );
            }
          );
        }
      );
    }
  );
}

function syncNeighborEdge(
  neighborCell:
    | HTMLTableCellElement
    | null
    | undefined,
  selectedCell:
    HTMLTableCellElement,
  selectedSet:
    Set<HTMLTableCellElement>,
  neighborSide:
    TableBorderSide,
  border: string,
  isRemoveBorder = false
): void {
  if (
    !neighborCell ||
    neighborCell === selectedCell ||
    selectedSet.has(neighborCell)
  ) {
    return;
  }

  /*
   * Before modifying an unselected neighbour,
   * preserve its original border.
   */
  rememberBaseTableCellBorders(
    neighborCell
  );

  setCellBorderSide(
    neighborCell,
    neighborSide,
    border
  );

  /*
   * Remove Border makes the neutral gridline the neighbour's
   * new base for this shared edge. Without re-basing, the
   * snapshot captured when the border was ADDED survives (it is
   * never overwritten) and a later restoreBaseTableCellBorders()
   * - triggered by toggling Highlighted/Thick off - resurrects
   * the removed black/dashed edge on the neighbour, which then
   * owns that edge again in the report grid.
   */
  if (isRemoveBorder) {
    neighborCell.setAttribute(
      `${tableBaseBorderAttributePrefix}${neighborSide}`,
      border
    );
  }
}


/* =========================================================
 * CELL STYLE
 * ========================================================= */

export function applyFroalaTableCellStyleToSelection(
  editor: any,
  styleClass: string
): boolean {
  if (
    !tableCellStyleClasses.includes(
      styleClass
    )
  ) {
    return false;
  }

  const selectedCells =
    getSelectedFroalaTableCells(
      editor
    );

  if (!selectedCells.length) {
    return false;
  }

  const shouldRemoveStyle =
    selectedCells.every(
      cell =>
        cell.classList.contains(
          styleClass
        )
    );

  selectedCells.forEach((cell) => {
    rememberBaseTableCellBorders(
      cell
    );

    tableCellStyleClasses.forEach(
      cellStyleClass => {
        cell.classList.remove(
          cellStyleClass
        );
      }
    );

    if (!shouldRemoveStyle) {
      cell.classList.add(
        styleClass
      );

      applyEffectiveTableCellBorder(
        cell
      );

      return;
    }

    if (
      hasAppliedTableBorderClass(
        cell
      )
    ) {
      applyEffectiveTableCellBorder(
        cell
      );

      return;
    }

    restoreBaseTableCellBorders(
      cell
    );
  });

  restoreFroalaTableSelectionAfterCommand(
    editor,
    selectedCells
  );

  return true;
}


/* =========================================================
 * VERTICAL ALIGNMENT
 * ========================================================= */

function normalizeFroalaTableVerticalAlign(
  value: string | null | undefined
): string | null {
  const normalizedValue =
    (value || '')
      .trim()
      .toLowerCase();

  return tableCellVerticalAlignValues.includes(
    normalizedValue
  )
    ? normalizedValue
    : null;
}

function getFroalaEditorRootElement(
  editorOrRoot: any
): HTMLElement | null {
  if (
    isHtmlElement(editorOrRoot)
  ) {
    return editorOrRoot;
  }

  return isHtmlElement(
    editorOrRoot?.el
  )
    ? editorOrRoot.el
    : null;
}

function applyFroalaTableCellVerticalAlign(
  cell: HTMLElement,
  verticalAlign: string,
  shouldRemoveSpacerContent = false
): void {
  tableCellVerticalAlignClasses.forEach(
    alignClass => {
      cell.classList.remove(
        alignClass
      );
    }
  );

  cell.classList.add(
    `${tableCellVerticalAlignClassPrefix}${verticalAlign}`
  );

  unwrapFroalaTableCellVerticalAlignContent(
    cell
  );

  if (shouldRemoveSpacerContent) {
    removeTableCellVerticalSpacerContent(
      cell
    );
  }

  cell.style.setProperty(
    'vertical-align',
    verticalAlign,
    'important'
  );

  cell.setAttribute(
    'valign',
    verticalAlign
  );
}

function unwrapFroalaTableCellVerticalAlignContent(
  cell: HTMLElement
): void {
  Array.from(
    cell.children
  ).forEach((child) => {
    if (
      !isHtmlElement(child) ||
      !child.classList.contains(
        tableCellVerticalAlignContentClass
      )
    ) {
      return;
    }

    while (child.firstChild) {
      cell.insertBefore(
        child.firstChild,
        child
      );
    }

    child.remove();
  });
}

function removeTableCellVerticalSpacerContent(
  cell: HTMLElement
): void {
  const childNodes =
    Array.from(
      cell.childNodes
    );

  const hasMeaningfulContent =
    childNodes.some(
      node =>
        isMeaningfulTableCellContent(
          node
        )
    );

  if (!hasMeaningfulContent) {
    return;
  }

  const spacerNodes =
    getTableCellEdgeSpacerNodes(
      cell
    );

  if (!spacerNodes.length) {
    return;
  }

  preserveTableCellCurrentHeight(
    cell
  );

  spacerNodes.forEach((node) => {
    if (
      node.parentNode === cell
    ) {
      cell.removeChild(
        node
      );
    }
  });
}

function getTableCellEdgeSpacerNodes(
  cell: HTMLElement
): ChildNode[] {
  const childNodes =
    Array.from(
      cell.childNodes
    );

  const spacerNodes:
    ChildNode[] = [];

  for (
    const node of childNodes
  ) {
    if (
      !isTableCellSpacerContent(
        node
      )
    ) {
      break;
    }

    spacerNodes.push(
      node
    );
  }

  for (
    let index =
      childNodes.length - 1;
    index >= 0;
    index--
  ) {
    const node =
      childNodes[index];

    if (
      !isTableCellSpacerContent(
        node
      )
    ) {
      break;
    }

    spacerNodes.push(
      node
    );
  }

  return spacerNodes;
}

function preserveTableCellCurrentHeight(
  cell: HTMLElement
): void {
  const currentHeight =
    Math.ceil(
      cell.getBoundingClientRect()
        .height
    );

  if (currentHeight > 0) {
    cell.style.setProperty(
      'height',
      `${currentHeight}px`
    );
  }
}

function isTableCellSpacerContent(
  node: ChildNode
): boolean {
  return !isMeaningfulTableCellContent(
    node
  );
}

function isMeaningfulTableCellContent(
  node: ChildNode
): boolean {
  if (
    node.nodeType ===
    Node.TEXT_NODE
  ) {
    return !!(
      node.textContent || ''
    )
      .replace(/\u00a0/g, '')
      .trim();
  }

  if (!isHtmlElement(node)) {
    return false;
  }

  if (
    node.classList.contains(
      'fr-marker'
    )
  ) {
    return true;
  }

  if (
    node.matches(
      'img, video, table, hr, iframe'
    )
  ) {
    return true;
  }

  if (
    (node.textContent || '')
      .replace(/\u00a0/g, '')
      .trim()
  ) {
    return true;
  }

  return Array.from(
    node.childNodes
  ).some(
    childNode =>
      isMeaningfulTableCellContent(
        childNode
      )
  );
}

export function applyFroalaTableVerticalAlignToSelection(
  editor: any,
  alignValue: string
): boolean {
  const verticalAlign =
    normalizeFroalaTableVerticalAlign(
      alignValue
    );

  if (!verticalAlign) {
    return false;
  }

  const selectedCells =
    getSelectedFroalaTableCells(
      editor
    );

  if (!selectedCells.length) {
    return false;
  }

  selectedCells.forEach((cell) => {
    applyFroalaTableCellVerticalAlign(
      cell,
      verticalAlign,
      true
    );
  });

  restoreFroalaTableSelectionAfterCommand(
    editor,
    selectedCells
  );

  return true;
}

export function normalizeFroalaTableVerticalAlignCells(
  editorOrRoot: any
): void {
  const root =
    getFroalaEditorRootElement(
      editorOrRoot
    );

  if (!root) {
    return;
  }

  const alignedCells =
    Array.from(
      root.querySelectorAll(
        'td[valign], ' +
        'th[valign], ' +
        'td[style*="vertical-align"], ' +
        'th[style*="vertical-align"], ' +
        'td[class*="blueprint-cell-valign-"], ' +
        'th[class*="blueprint-cell-valign-"]'
      )
    ) as HTMLElement[];

  alignedCells.forEach((cell) => {
    const verticalAlign =
      getExplicitCellVerticalAlign(
        cell
      );

    if (verticalAlign) {
      applyFroalaTableCellVerticalAlign(
        cell,
        verticalAlign
      );
    }
  });
}

function getExplicitCellVerticalAlign(
  cell: HTMLElement
): string | null {
  return (
    normalizeFroalaTableVerticalAlign(
      cell.style.getPropertyValue(
        'vertical-align'
      ) ||
      cell.style.verticalAlign
    ) ||
    normalizeFroalaTableVerticalAlign(
      cell.getAttribute('valign')
    ) ||
    tableCellVerticalAlignValues.find(
      alignValue =>
        cell.classList.contains(
          `${tableCellVerticalAlignClassPrefix}${alignValue}`
        )
    ) ||
    null
  );
}

function getCellVerticalAlign(
  cell: HTMLElement
): string {
  return (
    getExplicitCellVerticalAlign(
      cell
    ) || 'top'
  );
}


/* =========================================================
 * ACTIVE STYLE HELPERS
 * ========================================================= */

function getActiveSelectedCellBorderClass(
  editor: any
): string | null {
  const selectedCells =
    getSelectedFroalaTableCells(
      editor
    );

  if (!selectedCells.length) {
    return null;
  }

  return tableBorderStyleClasses.find(
    styleClass =>
      selectedCells.every(
        cell =>
          cell.classList.contains(
            styleClass
          )
      )
  ) || null;
}

function getActiveSelectedCellStyleClass(
  editor: any
): string | null {
  const selectedCells =
    getSelectedFroalaTableCells(
      editor
    );

  if (!selectedCells.length) {
    return null;
  }

  return tableCellStyleClasses.find(
    styleClass =>
      selectedCells.every(
        cell =>
          cell.classList.contains(
            styleClass
          )
      )
  ) || null;
}

function getActiveSelectedCellVerticalAlign(
  editor: any
): string | null {
  const selectedCells =
    getSelectedFroalaTableCells(
      editor
    );

  if (!selectedCells.length) {
    return null;
  }

  const activeAlign =
    getCellVerticalAlign(
      selectedCells[0]
    );

  return selectedCells.every(
    cell =>
      getCellVerticalAlign(cell) ===
      activeAlign
  )
    ? activeAlign
    : null;
}


/* =========================================================
 * TABLE GRID
 * ========================================================= */

function buildTableGrid(
  table: HTMLTableElement
): TableGrid {
  const grid: TableGrid = [];

  const rows =
    Array.from(table.rows);

  rows.forEach(
    (row, rowIndex) => {
      grid[rowIndex] =
        grid[rowIndex] || [];

      let columnIndex = 0;

      Array.from(
        row.cells
      ).forEach((cell) => {
        while (
          grid[rowIndex][
            columnIndex
          ]
        ) {
          columnIndex++;
        }

        const rowSpan =
          Math.max(
            cell.rowSpan || 1,
            1
          );

        const colSpan =
          Math.max(
            cell.colSpan || 1,
            1
          );

        for (
          let rowOffset = 0;
          rowOffset < rowSpan;
          rowOffset++
        ) {
          const targetRowIndex =
            rowIndex +
            rowOffset;

          grid[targetRowIndex] =
            grid[targetRowIndex] ||
            [];

          for (
            let columnOffset = 0;
            columnOffset < colSpan;
            columnOffset++
          ) {
            grid[targetRowIndex][
              columnIndex +
              columnOffset
            ] = cell;
          }
        }

        columnIndex += colSpan;
      });
    }
  );

  return grid;
}


/* =========================================================
 * FROALA COMMAND REGISTRATION
 * ========================================================= */

export function registerFroalaTableBorderCommand(): void {
  if (
    typeof FroalaEditor ===
    'undefined'
  ) {
    return;
  }

  const tableStyleCommand = {
    title: 'Table Style',
    type: 'dropdown',
    focus: false,
    undo: true,

    html: function(
      this: any
    ): string {
      let dropdownHtml =
        '<ul class="fr-dropdown-list" role="presentation">';

      Object.keys(
        tableBorderStyleLabels
      ).forEach(
        (styleClass) => {
          const label =
            this.language.translate(
              tableBorderStyleLabels[
                styleClass
              ]
            );

          dropdownHtml +=
            `<li role="presentation">` +
            `<a class="fr-command" ` +
            `tabIndex="-1" ` +
            `role="option" ` +
            `data-cmd="tableStyle" ` +
            `data-param1="${styleClass}" ` +
            `title="${label}">` +
            `${label}` +
            `</a>` +
            `</li>`;
        }
      );

      return (
        `${dropdownHtml}</ul>`
      );
    },

    callback: function(
      this: any,
      _command: string,
      styleClass: string
    ): void {
      this.undo?.saveStep?.();

      const didApply =
        applyFroalaTableBorderStyleToSelection(
          this,
          styleClass
        );

      if (!didApply) {
        return;
      }

      this.undo?.saveStep?.();

      this.events?.trigger?.(
        'contentChanged'
      );
    },

    refreshOnShow: function(
      this: any,
      _button: any,
      dropdown: any
    ): void {
      const activeStyleClass =
        getActiveSelectedCellBorderClass(
          this
        );

      const $ = this.$;

      dropdown
        .find('.fr-command')
        .each(
          function(
            this: HTMLElement
          ) {
            const commandElement =
              $(this);

            const isActive =
              activeStyleClass ===
              commandElement.data(
                'param1'
              );

            commandElement
              .toggleClass(
                'fr-active',
                isActive
              )
              .attr(
                'aria-selected',
                isActive
              );
          }
        );
    }
  };


  const tableCellStyleCommand = {
    title: 'Cell Style',
    type: 'dropdown',
    focus: false,
    undo: true,

    html: function(
      this: any
    ): string {
      let dropdownHtml =
        '<ul class="fr-dropdown-list" role="presentation">';

      Object.keys(
        tableCellStyleLabels
      ).forEach(
        (styleClass) => {
          const label =
            this.language.translate(
              tableCellStyleLabels[
                styleClass
              ]
            );

          dropdownHtml +=
            `<li role="presentation">` +
            `<a class="fr-command" ` +
            `tabIndex="-1" ` +
            `role="option" ` +
            `data-cmd="tableCellStyle" ` +
            `data-param1="${styleClass}" ` +
            `title="${label}">` +
            `${label}` +
            `</a>` +
            `</li>`;
        }
      );

      return (
        `${dropdownHtml}</ul>`
      );
    },

    callback: function(
      this: any,
      _command: string,
      styleClass: string
    ): void {
      this.undo?.saveStep?.();

      const didApplyCellStyle =
        applyFroalaTableCellStyleToSelection(
          this,
          styleClass
        );

      if (!didApplyCellStyle) {
        return;
      }

      this.undo?.saveStep?.();

      this.events?.trigger?.(
        'contentChanged'
      );
    },

    refreshOnShow: function(
      this: any,
      _button: any,
      dropdown: any
    ): void {
      const activeStyleClass =
        getActiveSelectedCellStyleClass(
          this
        );

      const $ = this.$;

      dropdown
        .find('.fr-command')
        .each(
          function(
            this: HTMLElement
          ) {
            const commandElement =
              $(this);

            const isActive =
              activeStyleClass ===
              commandElement.data(
                'param1'
              );

            commandElement
              .toggleClass(
                'fr-active',
                isActive
              )
              .attr(
                'aria-selected',
                isActive
              );
          }
        );
    }
  };


  const tableCellVerticalAlignCommand = {
    title: 'Vertical Align',
    type: 'dropdown',
    focus: false,
    undo: true,

    html: function(
      this: any
    ): string {
      let dropdownHtml =
        '<ul class="fr-dropdown-list" role="presentation">';

      Object.keys(
        tableCellVerticalAlignLabels
      ).forEach(
        (alignValue) => {
          const label =
            this.language.translate(
              tableCellVerticalAlignLabels[
                alignValue
              ]
            );

          dropdownHtml +=
            `<li role="presentation">` +
            `<a class="fr-command" ` +
            `tabIndex="-1" ` +
            `role="option" ` +
            `data-cmd="tableCellVerticalAlign" ` +
            `data-param1="${alignValue}" ` +
            `title="${label}">` +
            `${label}` +
            `</a>` +
            `</li>`;
        }
      );

      return (
        `${dropdownHtml}</ul>`
      );
    },

    callback: function(
      this: any,
      _command: string,
      alignValue: string
    ): void {
      this.undo?.saveStep?.();

      const didApplyVerticalAlign =
        applyFroalaTableVerticalAlignToSelection(
          this,
          alignValue
        );

      if (!didApplyVerticalAlign) {
        return;
      }

      this.undo?.saveStep?.();

      this.events?.trigger?.(
        'contentChanged'
      );
    },

    refreshOnShow: function(
      this: any,
      _button: any,
      dropdown: any
    ): void {
      const activeAlign =
        getActiveSelectedCellVerticalAlign(
          this
        ) || 'top';

      dropdown
        .find('.fr-command')
        .removeClass(
          'fr-active'
        )
        .attr(
          'aria-selected',
          false
        );

      dropdown
        .find(
          `.fr-command[data-param1="${activeAlign}"]`
        )
        .addClass(
          'fr-active'
        )
        .attr(
          'aria-selected',
          true
        );
    }
  };


  if (
    typeof FroalaEditor.RegisterCommand ===
    'function'
  ) {
    FroalaEditor.RegisterCommand(
      'tableStyle',
      tableStyleCommand
    );

    FroalaEditor.RegisterCommand(
      'tableCellStyle',
      tableCellStyleCommand
    );

    FroalaEditor.RegisterCommand(
      'tableCellVerticalAlign',
      tableCellVerticalAlignCommand
    );
  }

  if (FroalaEditor.COMMANDS) {
    FroalaEditor.COMMANDS.tableStyle =
      tableStyleCommand;

    FroalaEditor.COMMANDS.tableCellStyle =
      tableCellStyleCommand;

    FroalaEditor.COMMANDS.tableCellVerticalAlign =
      tableCellVerticalAlignCommand;
  }
}